// web/src/components/ModuleMapModal/modal.tsx —— 对外：ModuleMapModalProps 与 default 导出的弹层主体
// 拆自 web/src/components/ModuleMapModal.tsx 的同名分区（内容原样搬运）；由 web/src/components/ModuleMapModal.tsx 以 export * 聚合，导出面对调用方不变。

import { useEffect, useMemo, useRef, useState, type ComponentRef, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Input, Button, Message } from '@arco-design/web-react';
import {
  IconApps,
  IconCloud,
  IconCustomerService,
  IconDesktop,
  IconFile,
  IconHistory,
  IconHome,
  IconSearch,
  IconSettings,
  IconThunderbolt,
} from '@arco-design/web-react/icon';
import { NAV_ANCHORS, featureByPathname, groupedNav, type Feature, type NavAnchor } from '../../registry/features';
import { canSeeEntry, canSeePermission } from '../../registry/access';
import {
  buildSearchIndex,
  commonEntries,
  searchFeatureIndex,
  suggestSimilar,
  type EntryKind,
  type SearchEntry,
  type SearchHit,
} from '../../registry/featureSearch';
import { tabsOf } from '../../registry/navTabs';
import { useAuthStore } from '../../store/auth';
import { useAgentStore } from '../../store/agent';
import { DOMAIN_ICONS, MENU_ICONS } from '../../layouts/menuIcons';
import FormModal, { FORM_MODAL_WIDTH } from '../FormModal';
import PillTag, { type PillColor } from '../PillTag';
import EmptyState from '../EmptyState';

import { highlight } from './_highlight';
import type { FeatureModule, FeaturePoint, MapSection } from './_model';
import { ENTRY_KIND, RECENT_MAX, RECENT_STORAGE, SECTION_ICONS, STRONG_CAP, TAB_PREVIEW, canSee, canSeeAnchor, loadRecent } from './_model';

export interface ModuleMapModalProps {
  visible: boolean;
  onClose: () => void;
}

export default function ModuleMapModal({ visible, onClose }: ModuleMapModalProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const role = useAuthStore((s) => s.user?.role) || 'agent';
  const perms = useAuthStore((s) => s.rbac?.permissions);
  const permSet = useMemo(() => new Set(perms || []), [perms]);
  // 坐席绑定态（与侧栏同源）：`requiresAgent` 条目（坐席工作台）未绑档案时不进索引与浏览态
  const agentBound = useAgentStore((s) => !!s.agentId);
  const sections = useMemo(() => groupedNav(), []);
  const [keyword, setKeyword] = useState('');
  const [recent, setRecent] = useState<string[]>([]);
  const [cursor, setCursor] = useState(0);
  /** 分区筛选（浏览态）：'all' 或分区 id，只影响「看哪一段」，不改变索引与检索 */
  const [sectionFilter, setSectionFilter] = useState('all');
  /** 已展开全部页内 Tab 的功能点（键 `<moduleKey>#<featureId>`） */
  const [openedTabs, setOpenedTabs] = useState<Record<string, boolean>>({});
  const [showWeak, setShowWeak] = useState(false);
  const searchRef = useRef<ComponentRef<typeof Input>>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // 打开（Ctrl+M 或点击搜索入口）后回到初始态：清关键词、载入最近访问、复位筛选与展开，并自动聚焦搜索框
  useEffect(() => {
    if (!visible) return;
    setKeyword('');
    setCursor(0);
    setSectionFilter('all');
    setOpenedTabs({});
    setShowWeak(false);
    setRecent(loadRecent());
    const t = window.setTimeout(() => searchRef.current?.focus?.(), 60);
    return () => window.clearTimeout(t);
  }, [visible]);

  const index = useMemo(
    () => buildSearchIndex().filter((e) => canSeeEntry(e, role, permSet, { agentBound })),
    [role, permSet, agentBound],
  );
  const entryByKey = useMemo(() => new Map(index.map((e) => [e.key, e])), [index]);

  const hits = useMemo(() => searchFeatureIndex(index, keyword), [index, keyword]);
  const searching = keyword.trim().length > 0;
  const suggestions = useMemo(
    () => (searching && hits.length === 0 ? suggestSimilar(index, keyword, 3) : []),
    [searching, hits.length, index, keyword],
  );
  /** 零结果是死路就没有兜底：再给一组「从这些入口开始」 */
  const fallbackEntries = useMemo(() => commonEntries(index, 6), [index]);

  /**
   * 结果分层：强匹配（名称/别名/动作）优先展示，弱匹配（只是归属或说明里出现过）默认收成一行。
   * 实测「设置」能命中 29 条、「客户」23 条——平铺出来反而让人无从选择；
   * 强匹配另设上限 10 条，超出提示继续输入缩小范围（结果多了要能"收"，不是"堆"）。
   */
  const strongHits = useMemo(() => hits.filter((h) => h.strong), [hits]);
  const weakHits = useMemo(() => hits.filter((h) => !h.strong), [hits]);
  const strongShown = strongHits.slice(0, STRONG_CAP);
  const hiddenStrong = strongHits.length - strongShown.length;
  /** 没有任何强匹配时（整句/口语化输入）弱匹配就是答案，直接展开，否则用户会以为"没搜到" */
  const weakVisible = strongHits.length === 0 || showWeak;
  const visibleHits = useMemo(
    () => (weakVisible ? [...strongShown, ...weakHits] : strongShown),
    [weakVisible, strongShown, weakHits],
  );

  /** 浏览态：顶层锚点单独成段，其余按「分区 → 业务域」成泳道（与侧栏同源） */
  const mapSections = useMemo<MapSection[]>(() => {
    const out: MapSection[] = [];
    const entries: FeatureModule[] = [];
    for (const a of NAV_ANCHORS) {
      if (!canSeeAnchor(a, role, permSet, agentBound)) continue;
      const af = featureByPathname(a.route);
      entries.push({
        key: `anchor:${a.route}`,
        title: a.title,
        icon: MENU_ICONS[a.route] ?? <IconApps />,
        features: [
          { id: a.id, entryKey: `anchor:${a.route}`, name: a.title, path: a.route, tabs: af ? tabsOf(af.id) : [] },
        ],
      });
    }
    if (entries.length > 0) {
      out.push({ id: 'entry', title: '顶层入口', icon: <IconHome />, modules: entries });
    }
    for (const s of sections) {
      const modules: FeatureModule[] = [];
      for (const g of s.groups) {
        const items = g.items.filter((f) => canSee(f, role, permSet));
        if (items.length === 0) continue;
        modules.push({
          key: `${s.section}:${g.key}`,
          title: g.title || s.title,
          icon: DOMAIN_ICONS[g.key] ?? SECTION_ICONS[s.section],
          features: items.map((f) => ({
            id: f.id,
            entryKey: `feature:${f.id}`,
            name: f.title,
            path: f.route,
            tabs: tabsOf(f.id),
          })),
        });
      }
      if (modules.length > 0) {
        out.push({ id: s.section, title: s.title, icon: SECTION_ICONS[s.section] ?? <IconApps />, modules });
      }
    }
    return out;
  }, [sections, role, permSet, agentBound]);

  /** 头部统计（模块/功能/页内 Tab 数）与分区筛选计数：让「这里到底有多少东西」一眼可见 */
  const sectionStats = useMemo(() => {
    let features = 0;
    let tabs = 0;
    for (const s of mapSections) {
      for (const m of s.modules) {
        features += m.features.length;
        for (const f of m.features) tabs += f.tabs.length;
      }
    }
    return { features, tabs };
  }, [mapSections]);
  const totalModules = useMemo(() => mapSections.reduce((n, s) => n + s.modules.length, 0), [mapSections]);
  const shownSections = useMemo(
    () => (sectionFilter === 'all' ? mapSections : mapSections.filter((s) => s.id === sectionFilter)),
    [mapSections, sectionFilter],
  );

  const activeKey = useMemo(() => {
    const anchor = NAV_ANCHORS.find((a) => a.route === pathname);
    if (anchor) return `anchor:${anchor.route}`;
    const f = featureByPathname(pathname);
    if (!f) return '';
    return (
      mapSections.flatMap((s) => s.modules).find((m) => m.features.some((p) => p.path === f.route))?.key || ''
    );
  }, [pathname, mapSections]);

  const go = (route: string, key?: string) => {
    if (key) {
      const next = [key, ...loadRecent().filter((k) => k !== key)].slice(0, RECENT_MAX);
      localStorage.setItem(RECENT_STORAGE, JSON.stringify(next));
    }
    navigate(route);
    onClose();
  };

  /** 全局动作：没有页面可跳（改密码、切状态…），只回答「在哪儿」，故不关弹窗 */
  const openAction = (e: SearchEntry) => {
    Message.info(`「${e.name}」的位置：${e.hint ?? ''}`);
  };

  /** 结果列表键盘操作：↑↓ 移动、Enter 跳转（Esc 关弹窗由 FormModal 负责）。
   *  只在**当前可见**的结果里移动（弱匹配收起时不该被键盘选中看不见的项）。 */
  useEffect(() => {
    if (!visible || !searching) return;
    const onKey = (e: KeyboardEvent) => {
      if (visibleHits.length === 0) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setCursor((c) =>
          e.key === 'ArrowDown' ? (c + 1) % visibleHits.length : (c - 1 + visibleHits.length) % visibleHits.length,
        );
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const hit = visibleHits[Math.min(cursor, visibleHits.length - 1)];
        if (!hit) return;
        if (hit.entry.kind === 'action') openAction(hit.entry);
        else go(hit.entry.route, hit.entry.key);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, searching, visibleHits, cursor]);

  // 换关键词就回到第一条、并把「相关结果」收回——上一次的选中位置对新结果没有意义
  useEffect(() => {
    setCursor(0);
    setShowWeak(false);
  }, [keyword]);

  // 键盘移动后把选中项滚入可视
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>('.bcc-help-result-active');
    el?.scrollIntoView({ block: 'nearest' });
  }, [cursor, visibleHits]);

  const recentEntries = recent.map((k) => entryByKey.get(k)).filter((e): e is SearchEntry => !!e);

  /** 一个功能点 = 功能名胶囊 + 其页内 Tab（超出 4 个收成「+N」） */
  const featureBlock = (module: FeatureModule, f: FeaturePoint) => {
    const openKey = `${module.key}#${f.id}`;
    const expanded = !!openedTabs[openKey];
    const tabs = expanded ? f.tabs : f.tabs.slice(0, TAB_PREVIEW);
    const hidden = f.tabs.length - tabs.length;
    return (
      <div key={f.id} className="bcc-help-feature-block">
        <span className="bcc-help-feature" title={`${f.name}（点击跳转）`} onClick={() => go(f.path, f.entryKey)}>
          {f.name}
        </span>
        {f.tabs.length > 0 && (
          <div className="bcc-help-feature-tabs">
            {tabs.map((t) => (
              <span
                key={t.key}
                className="bcc-help-feature-tab"
                title={`${f.name} · ${t.title}`}
                onClick={() => go(`${f.path}?tab=${t.key}`, `${f.id}#${t.key}`)}
              >
                {t.title}
              </span>
            ))}
            {(hidden > 0 || expanded) && f.tabs.length > TAB_PREVIEW && (
              <span
                className="bcc-help-feature-tab bcc-help-feature-tab-more"
                title={expanded ? '收起页内 Tab' : `展开另外 ${hidden} 个页内 Tab`}
                onClick={() => setOpenedTabs((m) => ({ ...m, [openKey]: !expanded }))}
              >
                {expanded ? '收起' : `+${hidden}`}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  const resultRow = (hit: SearchHit, idx: number) => {
    const e = hit.entry;
    const isAction = e.kind === 'action';
    const head = ENTRY_KIND[e.kind];
    const active = idx === cursor;
    return (
      <div
        key={e.key}
        className={`bcc-help-result${active ? ' bcc-help-result-active' : ''}`}
        onMouseEnter={() => setCursor(idx)}
        onClick={() => (isAction ? openAction(e) : go(e.route, e.key))}
        title={isAction ? `位置：${e.hint}` : `${e.display}（回车跳转）`}
      >
        <span className="bcc-help-result-icon">{head.icon}</span>
        <div className="bcc-help-result-body">
          <div className="bcc-help-result-main">
            <span className="bcc-help-result-name">{highlight(e.name, hit.marks)}</span>
            {head.label && (
              <span className="bcc-help-result-kind">
                <PillTag size="sm" color={head.color}>
                  {head.label}
                </PillTag>
              </span>
            )}
            {active && <span className="bcc-help-result-enter">回车跳转 ↵</span>}
          </div>
          <div className="bcc-help-result-meta">
            <span className="bcc-help-result-crumb">{isAction ? `位置：${e.hint}` : e.breadcrumb}</span>
            {!isAction && e.display !== e.name && <span className="bcc-help-result-crumb">· {e.display}</span>}
            {hit.reason && hit.reason !== '命中功能名' && (
              <span className="bcc-help-result-reason">{hit.reason}</span>
            )}
          </div>
          {e.desc && <div className="bcc-help-result-desc">{e.desc}</div>}
        </div>
      </div>
    );
  };

  return (
    <FormModal
      className="bcc-help-modal"
      visible={visible}
      onCancel={onClose}
      title={null}
      width={FORM_MODAL_WIDTH.sheet}
      footer={
        <div className="bcc-help-foot">
          <span className="bcc-help-foot-tip">
            {searching && (
              <>
                <kbd>↑</kbd>
                <kbd>↓</kbd> 选择 · <kbd>Enter</kbd> 跳转 ·{' '}
              </>
            )}
            <kbd>Esc</kbd> 关闭
          </span>
          <Button onClick={onClose}>关闭</Button>
        </div>
      }
      // 走 FormModal 默认的限高滚动壳（.bcc-modal-body）：帮助弹窗靠
      // `.bcc-help-modal .bcc-modal-body`（components.css）取固定高度，搜索过滤时弹窗不忽大忽小。
    >
      <div className="bcc-help-top">
        <div className="bcc-help-head">
          <div className="bcc-help-head-main">
            <span className="bcc-help-head-icon">
              <IconApps />
            </span>
            <div className="bcc-help-head-text">
              <span className="bcc-help-title">模块功能地图</span>
              <span className="bcc-help-head-stats">
                {totalModules} 个模块 · {sectionStats.features} 项功能 · {sectionStats.tabs} 个页内 Tab
              </span>
            </div>
          </div>
          <div className="bcc-help-search-row">
            <Input
              ref={searchRef}
              className="bcc-help-search"
              size="default"
              allowClear
              prefix={<IconSearch />}
              placeholder="搜功能 / 俗称 / 页内 Tab，如：拉黑 录音 路由"
              value={keyword}
              onChange={setKeyword}
              style={{ width: 300 }}
            />
            <span className="bcc-help-kbd" aria-label="快捷键 Ctrl+M">
              <kbd>Ctrl</kbd>
              <i>+</i>
              <kbd>M</kbd>
            </span>
          </div>
        </div>

        {!searching && (
          <div className="bcc-help-toolbar">
            <div className="bcc-help-filters">
              <button
                type="button"
                className={`bcc-help-filter${sectionFilter === 'all' ? ' bcc-help-filter-active' : ''}`}
                onClick={() => setSectionFilter('all')}
              >
                全部
                <i>{totalModules}</i>
              </button>
              {mapSections.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className={`bcc-help-filter${sectionFilter === s.id ? ' bcc-help-filter-active' : ''}`}
                  onClick={() => setSectionFilter(s.id)}
                >
                  {s.title}
                  <i>{s.modules.length}</i>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="bcc-help-body">
        {searching ? (
          <div className="bcc-help-results" ref={listRef}>
            {hits.length > 0 && (
              <div className="bcc-help-results-count">
                {strongHits.length > 0
                  ? `${strongHits.length} 项匹配${weakHits.length ? ` · 另有 ${weakHits.length} 项相关` : ''}`
                  : `${weakHits.length} 项相关（没有精确匹配，按归属/说明给出）`}
              </div>
            )}
            {visibleHits.map((h, i) => resultRow(h, i))}
            {hiddenStrong > 0 && (
              <div className="bcc-help-suggest-note">还有 {hiddenStrong} 项匹配未显示，继续输入可以缩小范围。</div>
            )}
            {strongHits.length > 0 && weakHits.length > 0 && (
              <div className="bcc-help-more" onClick={() => setShowWeak((v) => !v)}>
                {showWeak ? '收起相关结果' : `展开另外 ${weakHits.length} 项相关（只命中了归属或说明）`}
              </div>
            )}
            {hits.length === 0 && (
              <div className="bcc-help-empty">
                <EmptyState icon={<IconSearch />} description="没有匹配的功能" />
                {suggestions.length > 0 && (
                  <div className="bcc-help-suggest">
                    <span className="bcc-help-suggest-label">是不是要找：</span>
                    {suggestions.map((e) => (
                      <span key={e.key} className="bcc-help-suggest-item" onClick={() => go(e.route, e.key)}>
                        {e.display}
                      </span>
                    ))}
                  </div>
                )}
                <div className="bcc-help-suggest">
                  <span className="bcc-help-suggest-label">从这些入口开始：</span>
                  {fallbackEntries.map((e) => (
                    <span key={e.key} className="bcc-help-suggest-item" onClick={() => go(e.route, e.key)}>
                      {e.display}
                    </span>
                  ))}
                </div>
                <div className="bcc-help-suggest-note">搜不到？试试功能里的原词（俗称与动作词会在第二阶段补进索引）。</div>
              </div>
            )}
          </div>
        ) : (
          <>
            {recentEntries.length > 0 && (
              <div className="bcc-help-recent">
                <span className="bcc-help-recent-label">
                  <IconHistory /> 最近访问
                </span>
                {recentEntries.map((e) => (
                  <span key={e.key} className="bcc-help-recent-item" onClick={() => go(e.route, e.key)}>
                    {e.display}
                  </span>
                ))}
              </div>
            )}
            {shownSections.map((sec) => (
              <section key={sec.id} className={`bcc-help-section bcc-help-section--${sec.id}`}>
                <div className="bcc-help-section-head">
                  <span className="bcc-help-section-icon">{sec.icon}</span>
                  <span className="bcc-help-section-title">{sec.title}</span>
                  <span className="bcc-help-section-count">
                    {sec.modules.length} 个模块 · {sec.modules.reduce((n, m) => n + m.features.length, 0)} 项功能
                  </span>
                  <span className="bcc-help-section-line" />
                </div>
                <div className="bcc-help-grid">
                  {sec.modules.map((module) => (
                    <div
                      key={module.key}
                      className={`bcc-help-module${activeKey === module.key ? ' bcc-help-module-active' : ''}`}
                    >
                      <div className="bcc-help-module-head">
                        <span className="bcc-help-module-icon">{module.icon}</span>
                        <span className="bcc-help-module-title">{module.title}</span>
                        {activeKey === module.key && (
                          <PillTag size="sm" color="arcoblue">
                            当前
                          </PillTag>
                        )}
                      </div>
                      <div className="bcc-help-features">
                        {module.features.map((f) => featureBlock(module, f))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </>
        )}
      </div>
    </FormModal>
  );
}
